import React, { useRef, useState, useEffect } from 'react';
import {
  X,
  Package,
  Save,
  AlertCircle,
} from 'lucide-react';
import useTax from '../../../../hooks/useTax';

/**
 * Inventory Item Form Component
 *
 * Modal form for adding/editing inventory items with:
 * - Name, Category, UOM, Tax selection
 * - Current stock, Minimum stock levels
 * - Unit cost
 */

export default function InventoryItemForm({ item, uoms, categories, onSave, onClose }) {
  const isEditing = !!item;

  // Tax dropdown data — reuses the SAME hook/service as the Menu Item form (no new API)
  const { taxes } = useTax();
  const activeTaxes = (taxes || []).filter((t) => t.isActive);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    categoryId: null,
    idUom: null,
    currentStock: 0,
    minimumStock: 0,
    unitCost: 0,
    taxId: null,
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef(null);

  // Initialize form when editing
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        categoryId: item.categoryId ? Number(item.categoryId) : null,
        idUom: item.idUom ? Number(item.idUom) : null,
        currentStock: item.currentStock || 0,
        minimumStock: item.minimumStock || 0,
        unitCost: item.unitCost || 0,
        // Saved tax for this inventory item (preselected on edit)
        taxId: item.taxId != null ? Number(item.taxId) : null,
      });
    }
  }, [item]);

  // Preselect tax once the shared useTax data has loaded:
  //  • Add mode  → auto-select the default tax (is_default = true)
  //  • Edit mode → keep the item's saved tax; if none is assigned, fall back to default
  useEffect(() => {
    if (!activeTaxes.length) return;
    const defaultTax = activeTaxes.find((t) => t.isDefault ?? t.is_default);
    if (!defaultTax) return;
    setFormData((prev) =>
      prev.taxId != null ? prev : { ...prev, taxId: Number(defaultTax.id) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxes, item]);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Item name is required';
    }

    if (!formData.idUom) {
      newErrors.idUom = 'Please select a unit of measurement';
    }
      if (!formData.categoryId) {
      newErrors.categoryId = 'Please select a a category';
    }

    if (formData.unitCost < 0) {
      newErrors.unitCost = 'Unit cost cannot be negative';
    }

    if (formData.currentStock < 0) {
      newErrors.currentStock = 'Stock cannot be negative';
    }

    if (formData.minimumStock < 0) {
      newErrors.minimumStock = 'Minimum stock cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async (event) => {
    event?.preventDefault();

    if (!validate()) return;

    setIsSaving(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      onSave({
        ...item,
        ...formData,
        idUom: parseInt(formData.idUom, 10),
        categoryId: parseInt(formData.categoryId, 10),
        currentStock: parseFloat(formData.currentStock) || 0,
        minimumStock: parseFloat(formData.minimumStock) || 0,
        unitCost: parseFloat(formData.unitCost) || 0,
        taxId: formData.taxId ? Number(formData.taxId) : null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || isSaving) {
      return;
    }

    const tagName = event.target.tagName;
    if (tagName === 'TEXTAREA') {
      return;
    }

    event.preventDefault();
    formRef.current?.requestSubmit();
  };

  // ESC key closes the modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <form
        ref={formRef}
        onSubmit={handleSave}
        onKeyDown={handleFormKeyDown}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
              <Package size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {isEditing ? 'Edit Item' : 'Add New Item'}
              </h2>
              <p className="text-xs text-slate-500">
                {isEditing ? 'Update inventory item details' : 'Add a new raw material or ingredient'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Item Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Basmati Rice, Chicken, Onions"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'
              }`}
              autoFocus
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.name}
              </p>
            )}
          </div>

          {/* Category & UOM Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.categoryId ?? ""}
                onChange={(e) => handleChange("categoryId", Number(e.target.value))}
                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 ${
                  errors.categoryId
                    ? "border-red-300 bg-red-50 focus:ring-red-500"
                    : "border-slate-200 focus:ring-teal-500"
                }`}

>
  <option value="">Select Category</option>

  {categories.map((cat) => (
    <option key={cat.id} value={cat.id}>
      {cat.name}
    </option>
  ))}
</select>

{errors.categoryId && (
  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
    <AlertCircle size={12} />
    {errors.categoryId}
  </p>
)}



            </div>

            {/* UOM */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Unit of Measurement <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.idUom}
                onChange={(e) => handleChange('idUom', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white ${
                  errors.idUom ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
              >
                <option value="">Select UOM</option>
                {uoms.map(uom => (
                  <option key={uom.id} value={uom.id}>{uom.name} ({uom.symbol})</option>
                ))}
              </select>
              {errors.idUom && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {errors.idUom}
                </p>
              )}
            </div>
          </div>

          {/* Tax (same source & option format as the Menu Item form) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Tax
            </label>
            <select
              value={formData.taxId ?? ''}
              onChange={(e) => handleChange('taxId', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">No tax</option>
              {activeTaxes.map((tax) => (
                <option key={tax.id} value={tax.id}>
                  {tax.name} ({tax.rate}%)
                  {tax.centerGstRate && tax.stateGstRate
                    ? ` [CGST ${tax.centerGstRate}% + SGST ${tax.stateGstRate}%]`
                    : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Stock & Pricing</h3>
          </div>

          {/* Stock Levels Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Current Stock */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Current Stock
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.currentStock}
                  onChange={(e) => handleChange('currentStock', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    errors.currentStock ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                />
                {formData.idUom && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                    {uoms.find(u => u.id === parseInt(formData.idUom))?.symbol}
                  </span>
                )}
              </div>
              {errors.currentStock && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {errors.currentStock}
                </p>
              )}
            </div>

            {/* Minimum Stock */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Minimum Stock
                <span className="text-xs text-slate-400 ml-1">(Alert threshold)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.minimumStock}
                  onChange={(e) => handleChange('minimumStock', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    errors.minimumStock ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                />
                {formData.idUom && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                    {uoms.find(u => u.id === parseInt(formData.idUom))?.symbol}
                  </span>
                )}
              </div>
              {errors.minimumStock && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {errors.minimumStock}
                </p>
              )}
            </div>
          </div>

          {/* Unit Cost */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Unit Cost
              <span className="text-xs text-slate-400 ml-1">(Cost per unit for food cost calculation)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">₹</span>
              <input
                type="number"
                value={formData.unitCost}
                onChange={(e) => handleChange('unitCost', e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.unitCost ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
              />
              {formData.idUom && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                  per {uoms.find(u => u.id === parseInt(formData.idUom))?.symbol}
                </span>
              )}
            </div>
            {errors.unitCost && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.unitCost}
              </p>
            )}
          </div>

          {/* Info Box */}
          {!isEditing && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Tip:</strong> You can update stock levels later using "Update Stock" for purchases, wastage, or adjustments. This keeps a proper audit trail.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEditing ? 'Update Item' : 'Add Item'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}